import { makeAutoObservable } from "mobx";
import ApiStore from "./ApiStore";
import dayjs, { Dayjs } from 'dayjs';
import KanbanTaskStore, { KanbanTask, KanbanTaskComment, KanbanTaskStatus, makeSummary, NewKanbanTaskComment } from "./KanbanTaskStores";
import { Project } from "./ProjectsStore";
import { ProjectDocument } from "./ProjectDocumentationStore";
import { c } from "framer-motion/dist/types.d-Cjd591yU";
import { Attachment, ScheduledAndPriorityUpdate, TaskTimeLog, TaskTimeMetrics } from "../api/KanbanTaskApiClient";

export interface AnonymousAccess {
  id: string;
  jwtToken: string;
  endDate: Dayjs;
  createdAt: Dayjs;
  projectId: string;
  companyId: string;
  authorId: string;
}

class AnonymousStore {
  ApiStore!: ApiStore;
  accesses: AnonymousAccess[] = [];

  projectStatuses!: KanbanTaskStatus[];
  kanbanTasks: KanbanTask[] = [];
  deletedProjectTasks: KanbanTask[] = [];
  currentTask: KanbanTask | null = null;
  documents!: ProjectDocument[];
  attachments: Attachment[] = [];
  taskTimeMetrics: TaskTimeMetrics | null = null;
  taskTimeLogs: TaskTimeLog[] = [];
  projectCustomFields: any[] = [];
  taskCustomFieldValues: Record<string, any[]> = {};

  // Loaders variables
  private loadingCount = 0;
  isLoading: boolean = false;
  isTitleLoading: boolean = false;
  isDescriptionLoading: boolean = false;
  isStatusLoading: boolean = false;
  isLoadingAttachments: boolean = false;
  isScheduledAndPriorityLoading: boolean = false;
  isCustomFieldsLoading: boolean = false;
  isAttachmentDeleting: boolean = false;

  constructor(apiStore: ApiStore) {
    makeAutoObservable(this);
    this.ApiStore = apiStore;
    this.kanbanTasks = [];
    this.accesses = [];
    this.projectStatuses = [];
  }

  /* ---------- Setters ---------- */
  setLoading = (loading: boolean): void => {
    if (loading) {
      this.loadingCount++;
    } else {
      this.loadingCount = Math.max(0, this.loadingCount - 1);
    }

    this.isLoading = this.loadingCount > 0;
  }


  
  /* ---------- Getting methods ---------- */
  getDocumentationByProject = async (): Promise<ProjectDocument[]> => {
    this.isLoading = true;
    const tasks = await this.ApiStore.AnonymousApiClient.getDocumentationByProject().finally(() => {
      this.isLoading = false;
    });
    if (tasks && tasks.length) {
      this.documents = tasks.map((el: {
        deadLine: string; startDate: string;
      }) => {
        return {
          ...el,
          startDate: dayjs(el.startDate),
          dueDate: dayjs(el.deadLine)
        }
      });
    }
    return tasks;
  }

  getTaskById = async (taskId: string): Promise<KanbanTask | null> => {
    this.setLoading(true);
    try {
      this.currentTask = null;
      const task = await this.ApiStore.AnonymousApiClient.getTask(taskId);
      this.currentTask = task;
      return task;
    } catch (error) {
      console.error("Failed to load task changes:", error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  getTaskComments = async (taskIdOrPublicId: string): Promise<any> => {
    this.setLoading(true);
    const comments = await this.ApiStore.AnonymousApiClient.getTaskComments(taskIdOrPublicId).finally(() => this.setLoading(false));
    return comments;
  }

  getTaskStatuses = async (): Promise<any> => {
    this.setLoading(true);
    const statuses = await this.ApiStore.AnonymousApiClient.getProjectStatuses().finally(() => this.setLoading(false));
    this.projectStatuses = statuses;
    return statuses;
  }

  getTasks = async (): Promise<any[]> => {
    this.setLoading(true);
    const tasks = await this.ApiStore.AnonymousApiClient.getProjectTasks().finally(() => this.setLoading(false));
    this.kanbanTasks = tasks;
    return tasks;
  }

  getTaskCustomFieldValues = async (taskId: string): Promise<any> => {
    this.setLoading(true);
    const values = await this.ApiStore.AnonymousApiClient.getTaskFieldValues(taskId).finally(() => this.setLoading(false));
    return values;
  }

  getProjectCustomFields = async (projectId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const fields = await this.ApiStore.AnonymousApiClient.getProjectCustomFields(projectId);
      if (fields) {
        this.projectCustomFields = fields;
      }
    } catch (error) {
      console.error("Failed to load project custom fields:", error);
    } finally {
      this.setLoading(false);
    }
  }

  setTaskCustomFieldValue = async (taskId: string, fieldId: string, value: string, userId?: string): Promise<boolean> => {
    this.isCustomFieldsLoading = true;
    try {
      const result = await this.ApiStore.AnonymousApiClient.setTaskCustomFieldValue(taskId, fieldId, value, userId);
      if (result) {
        // Update local state
        await this.getTaskCustomFieldValues(taskId);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to set custom field value:", error);
      return false;
    } finally {
      this.isCustomFieldsLoading = false;
    }
  }

  getFieldValueForTask = (taskId: string, fieldId: string): any => {
    const values = this.taskCustomFieldValues[taskId] || [];
    const valueObj = values.find(v => v.customFieldId.toString() === fieldId.toString());
    return valueObj ? valueObj.value : null;
  }

  getTaskChanges = async (taskIdOrPublicId: string): Promise<any> => {
    this.setLoading(true);
    const changes = await this.ApiStore.AnonymousApiClient.getTaskChanges(taskIdOrPublicId).finally(() => this.setLoading(false));
    return changes;
  }

  getTaskTimeMetrics = async (taskId: string): Promise<any> => {
    this.setLoading(true);
    const metrics = await this.ApiStore.AnonymousApiClient.getTaskTimeMetrics(taskId).finally(() => this.setLoading(false));
    return metrics;
  }

  getAttachments = async (taskId: string): Promise<any[]> => {
    this.setLoading(true);
    const result = await this.ApiStore.AnonymousApiClient.getAttachments(taskId).finally(() => this.setLoading(false));
    return result;
  }

  getProjectTasks = async (projectId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const tasks = await this.ApiStore.AnonymousApiClient.getProjectTasks();
      if (tasks) {
        this.kanbanTasks = tasks;
      }
    } finally {
      this.setLoading(false);
    }
  }

  getDeletedProjectTasks = async (projectId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const tasks = await this.ApiStore.AnonymousApiClient.getDeletedTasks(projectId);
      if (tasks) {
        this.deletedProjectTasks = tasks;
      }
    } finally {
      this.setLoading(false);
    }
  }

  getProjectStatuses = async (): Promise<void> => {
    this.setLoading(true);
    try {
      const statuses = await this.ApiStore.AnonymousApiClient.getProjectStatuses();
      if (statuses) {
        this.projectStatuses = statuses.sort((a, b) => a.index - b.index);
      }
    } catch (error) {
      console.error("Failed to load project statuses:", error);
    } finally {
      this.setLoading(false);
    }
  }

  getTaskTimeLogs = async (taskId: string): Promise<TaskTimeLog[]> => {
    try {
      const logs = await this.ApiStore.KanbanTaskApiClient.getTaskTimeLogs(taskId);
      this.taskTimeLogs = logs;
      return logs;
    } catch (error) {
      console.error("Failed to load task time logs:", error);
      return [];
    }
  }

  exportDocumentation = async (projectId: string, typeDocument: 'pdf' | 'docx') => {
    this.isLoadingAttachments = true;
    try {
      const response = await this.ApiStore.AnonymousApiClient.ExportDocumentation(
        projectId,
        typeDocument
      );

      // Axios повертає Blob у data (за умови responseType: 'blob' у клієнті)
      const blob: Blob = response.data;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';                 // щоб не миготіло
      link.href = url;
      link.download = typeDocument === 'pdf' ? 'Documentation.pdf' : 'Documentation.docx';

      // ДОДАЄМО В DOM перед кліком — це критично
      document.body.appendChild(link);
      link.click();

      // Прибираємо
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading file:', error?.message || error);
      throw error;
    } finally {
      this.isLoadingAttachments = false;
    }
  }



  /* ---------- Posting methods ---------- */
  async moveTask(taskId: string, toStatusId: string, toIndex: number): Promise<void> {
    this.setLoading(true);
    try {
      await this.ApiStore.AnonymousApiClient.moveTask(taskId, toStatusId, toIndex);
      const projId = this.kanbanTasks.find(t => String(t.id) === String(taskId))?.projectId;
      if (projId) await this.getProjectTasks(String(projId));
    } finally {
      this.setLoading(false);
    }
  }

  createComment = async (comment: NewKanbanTaskComment): Promise<KanbanTaskComment> => {
    this.setLoading(true);
    const result = await this.ApiStore.AnonymousApiClient.createComment(comment).finally(() => this.setLoading(false));
    return result;
  }

  createTask = async (task: any): Promise<KanbanTask | null> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.AnonymousApiClient.createTask(task);
      if (result) {
        this.kanbanTasks = [...this.kanbanTasks, result];
        return result;
      }
      return null;
    } catch (error) {
      console.error("Error creating task:", error);
      return null;
    } finally {
      this.setLoading(false);
    }
  };

  updateTask = async (taskId: string, task: any): Promise<any> => {
    this.setLoading(true);
    const result = await this.ApiStore.AnonymousApiClient.updateTask(taskId, task).finally(() => this.setLoading(false));
    return result;
  }

  uploadTaskAttachment = async (taskId: string, file: File, confirmation: boolean = false): Promise<Attachment | null> => {
    this.setLoading(true);
    try {
      console.log(`Store: Uploading ${file.name} to task ${taskId}`);

      const taskExists = this.kanbanTasks.some(task => task.id.toString() === taskId);
      if (!taskExists) {
        console.error(`Store: Task ${taskId} not found in project tasks`);
        throw new Error('Task not found');
      }

      const result = await this.ApiStore.AnonymousApiClient.uploadTaskAttachment(taskId, file, confirmation);

      if (result) {
        console.log('Store: Upload successful:', result);
        if (!this.attachments.some(a => a.publicId === result.publicId)) {
          this.attachments.push(result);
        }
        return result;
      } else {
        console.error('Store: Upload failed, no result returned');
        return null;
      }
    } catch (error) {
      console.error("Store: Error uploading attachment:", error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  };

  logTimeForTask = async (
    taskId: string,
    time: string,
    description: string,
    userId?: string,
    endDate?: string
  ): Promise<boolean> => {
    //
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.logTimeForTask(
        taskId, time, description, userId, endDate
      );

      if (result) {
        // Update metrics and logs with the returned data
        if (result.metrics) {
          this.taskTimeMetrics = result.metrics;
        } else {
          // If metrics weren't returned, fetch them
          await this.getTaskTimeMetrics(taskId);
        }

        // Refresh the time logs
        await this.getTaskTimeLogs(taskId);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to log time for task:", error);
      return false;
    }
  }

  createStatus = async (name: string, projectId: string, isFinalStatus: boolean): Promise<KanbanTaskStatus | null> => {
    this.setLoading(true);
    try {
      // Calculate next index
      const nextIndex = this.projectStatuses.length;

      const result = await this.ApiStore.AnonymousApiClient.createStatus(name, projectId, nextIndex, isFinalStatus);
      if (result) {
        this.projectStatuses.push(result);
        this.projectStatuses = this.projectStatuses.sort((a, b) => a.index - b.index);
        return result;
      }
      return null;
    } catch (error) {
      console.error("Failed to create status:", error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }



  /* ---------- Patching methods ---------- */
  patchDocumentation = async (docId: string, patchData: any): Promise<any> => {
    this.setLoading(true);
    const result = await this.ApiStore.AnonymousApiClient.patchDocumentation(docId, patchData).finally(() => this.setLoading(false));
    return result;
  }

  updateTaskStatus = async (taskId: string, statusId: string): Promise<void> => {
    this.isStatusLoading = true;
    try {
      const updatedTask = await this.ApiStore.AnonymousApiClient.updateTaskStatus(taskId, statusId);
      if (updatedTask) {
        await this.getProjectTasks(updatedTask.projectId);
      }


    } finally {
      this.isStatusLoading = false;
    }
  }

  updateTaskOrder = async (taskId: string, newIndex: number): Promise<void> => {
    this.setLoading(true);
    try {
      const updatedTask = await this.ApiStore.AnonymousApiClient.updateTaskOrder(taskId, newIndex);
      if (updatedTask) {
        await this.getProjectTasks(updatedTask.projectId);
      }
    } finally {
      this.setLoading(false);
    }
  }

  updateTaskScheduledAndPriority = async (taskId: string, scheduledAndPriority: ScheduledAndPriorityUpdate): Promise<void> => {
    this.isScheduledAndPriorityLoading = true;
    try {
      const updatedTask = await this.ApiStore.AnonymousApiClient.updateTaskScheduledAndPriority(taskId, scheduledAndPriority);
      if (updatedTask) {
        // Replace old task with the new, fully-populated task
        this.kanbanTasks = this.kanbanTasks.map((t) =>
          t.id === updatedTask.id ? updatedTask : t
        );
      }
    } finally {
      this.isScheduledAndPriorityLoading = false;
    }
  }

  updateTaskDescription = async (taskId: string, description: string): Promise<void> => {
    this.isDescriptionLoading = true;
    try {
      const updatedTask = await this.ApiStore.AnonymousApiClient.updateTaskDescription(taskId, description);
      if (updatedTask) {
        const summary = makeSummary(description);
        const toPersist = { ...updatedTask, summary };

        const serverTask = await this.ApiStore.AnonymousApiClient.updateTask(taskId, toPersist as KanbanTask);

        const finalTask = serverTask ?? toPersist;

        this.kanbanTasks = this.kanbanTasks.map((t) =>
          String(t.id) === String(finalTask.id) ? (finalTask as KanbanTask) : t
        );
      }
    } catch (e) {
      console.error("Failed to update task description:", e);
    } finally {
      this.isDescriptionLoading = false;
    }
  };

  updateTaskTitle = async (taskId: string, title: string): Promise<void> => {
    const originalTasks = [...this.kanbanTasks];
    this.kanbanTasks = this.kanbanTasks.map(t =>
      t.id === taskId ? { ...t, title } : t
    );

    this.isTitleLoading = true;
    try {
      const updatedTask = await this.ApiStore.AnonymousApiClient.updateTaskTitle(taskId, title);
      if (updatedTask) {
        this.kanbanTasks = this.kanbanTasks.map(t =>
          t.id === updatedTask.id ? updatedTask : t
        );
      }
    } catch (error) {
      this.kanbanTasks = originalTasks;
      throw error;
    } finally {
      this.isTitleLoading = false;
    }
  }



  /* ---------- Puting methods ---------- */
  updateStatus = async (statusId: string, name: string, isFinalStatus: boolean): Promise<KanbanTaskStatus | null> => {
    this.setLoading(true);
    try {
      const status = this.projectStatuses.find(s => s.id === statusId);
      if (!status) {
        throw new Error(`Status with ID ${statusId} not found`);
      }

      const updatedStatus = { ...status, name, isFinalStatus };
      const result = await this.ApiStore.AnonymousApiClient.updateStatus(statusId, updatedStatus);

      if (result) {
        this.projectStatuses = this.projectStatuses.map(s =>
          s.id === statusId ? result : s
        );
        return result;
      }
      return null;
    } catch (error) {
      console.error("Failed to update status:", error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }



  /* ---------- Deleting methods ---------- */
  deleteAttachment = async (publicId: string, taskId: string): Promise<boolean> => {
    this.isAttachmentDeleting = true;
    try {
      console.log(`Store: Deleting attachment ${publicId}`);
      const result = await this.ApiStore.AnonymousApiClient.deleteFile(publicId);

      if (result) {
        console.log('Store: Delete successful');
        // Update attachments in the store
        this.attachments = this.attachments.filter((att) => att.publicId !== publicId);
        return true;
      } else {
        console.error('Store: Delete failed');
        return false;
      }
    } catch (error) {
      console.error("Store: Error deleting attachment:", error);
      return false;
    } finally {
      this.isAttachmentDeleting = false;
    }
  };
}

export default AnonymousStore;
